import { useParams } from 'react-router-dom';
import { useOne, useCustom, useGo, useList } from '@refinedev/core';
import { Loader2, Tag, ArrowLeft, ExternalLink, User as UserIcon, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/tables';
import { resolveApiUrl } from '@/providers/api-url';
import type { TagItem } from './types';
import { TagAssignmentsCards } from './components';
import { useState } from 'react';

// ─── Типы ──────────────────────────────────────────────────────────────────

interface UserRow {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  isEmailVerified: boolean;
  createdAt: string;
}

// Тип, который возвращает бэкенд
interface RawEmailWithoutInvitationRow {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  createdAt: string;
}

// DataTable требует { id: number | string }
interface EmailWithoutInvitationRow extends RawEmailWithoutInvitationRow {
  id: number;
}

// ─── Вспомогательные ──────────────────────────────────────────────────────

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function buildUserName(row: Pick<UserRow, 'firstName' | 'lastName' | 'username' | 'id'>): string {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') || row.username || `#${row.id}`;
}

const CHANNEL_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  TELEGRAM: 'Telegram',
  GOOGLE: 'Google',
};

// ─── Вкладка «Основное» ────────────────────────────────────────────────────

function TagOverviewTab({ tag }: { tag: TagItem }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Параметры тега</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div className="text-muted-foreground">Название</div>
          <div className="font-medium">{tag.name}</div>

          <div className="text-muted-foreground">Цвет</div>
          <div className="flex items-center gap-2">
            <div
              className="h-5 w-5 rounded border border-border shrink-0"
              style={{ backgroundColor: tag.color }}
            />
            <span className="font-mono text-xs">{tag.color}</span>
          </div>

          <div className="text-muted-foreground">Описание</div>
          <div>{tag.description ?? <span className="text-muted-foreground">—</span>}</div>

          <div className="text-muted-foreground">Статус</div>
          <div>
            <Badge variant={tag.deletedAt ? 'outline' : 'default'}>
              {tag.deletedAt ? 'Архивирован' : 'Активный'}
            </Badge>
          </div>

          <div className="text-muted-foreground">Языки</div>
          <div>
            {tag.languages && tag.languages.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {tag.languages.map((l) => (
                  <Badge key={l} variant="secondary" className="font-mono text-xs">
                    {l.toUpperCase()}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">Все языки</span>
            )}
          </div>

          <div className="text-muted-foreground">Привязка аккаунта</div>
          <div>
            {tag.requiredChannels && tag.requiredChannels.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {tag.requiredChannels.map((ch) => (
                  <Badge key={ch} variant="secondary">
                    {CHANNEL_LABELS[ch] ?? ch}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">Не требуется</span>
            )}
          </div>

          <div className="text-muted-foreground">OTP для Email</div>
          <div>
            {tag.requiredChannels?.includes('EMAIL') ? (
              tag.skipEmailOtp ? (
                <Badge variant="outline">Без OTP</Badge>
              ) : (
                <Badge variant="default">OTP обязателен</Badge>
              )
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>

          <div className="text-muted-foreground">«Больше не показывать»</div>
          <div>
            {tag.requiredChannels && tag.requiredChannels.length > 0 ? (
              tag.linkModalDismissible ? (
                <Badge variant="outline">Разрешено</Badge>
              ) : (
                <Badge variant="default">Запрещено</Badge>
              )
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>

          <div className="text-muted-foreground">Создан</div>
          <div>{formatDate(tag.createdAt)}</div>

          <div className="text-muted-foreground">Обновлён</div>
          <div>{formatDate(tag.updatedAt)}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Вкладка «Привязки» ────────────────────────────────────────────────────

function TagAssignmentsTab({ tagId }: { tagId: number }) {
  const [userPage, setUserPage] = useState(1);
  const userPageSize = 15;

  const usersResult = useList<UserRow>({
    resource: 'users',
    pagination: { currentPage: userPage, pageSize: userPageSize, mode: 'server' },
    filters: [{ field: 'tagIds', operator: 'eq' as const, value: String(tagId) }],
    sorters: [{ field: 'createdAt', order: 'desc' }],
  });

  const users = usersResult.query.data?.data ?? [];
  const usersTotal = usersResult.query.data?.total ?? 0;
  const isLoadingUsers = usersResult.query.isLoading;

  const userColumns: Column<UserRow>[] = [
    {
      key: 'firstName',
      header: 'Пользователь',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <UserIcon className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="min-w-0">
            <a
              href={`/users/show/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline truncate flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {buildUserName(row)}
              <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
            </a>
            {row.username && (
              <div className="text-xs text-muted-foreground">@{row.username}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (_, row) => {
        if (!row.email) { return <span className="text-muted-foreground">—</span>; }
        return (
          <div className="flex items-center gap-1.5">
            <Mail className={`h-3.5 w-3.5 shrink-0 ${row.isEmailVerified ? 'text-green-500' : 'text-muted-foreground'}`} />
            <span className="text-sm truncate max-w-[200px]">{row.email}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Регистрация',
      width: '120px',
      render: (value) => <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Пользователи с этим тегом */}
      <div>
        <h3 className="text-sm font-semibold mb-3">
          Пользователи
          {usersTotal > 0 && (
            <Badge variant="secondary" className="ml-2 font-mono">{usersTotal}</Badge>
          )}
        </h3>
        <DataTable
          columns={userColumns}
          data={users}
          total={usersTotal}
          page={userPage}
          pageSize={userPageSize}
          isLoading={isLoadingUsers}
          onPageChange={setUserPage}
          emptyMessage="Нет пользователей с этим тегом"
          rowKey="id"
        />
      </div>

      {/* Контентные привязки — переиспользуем TagAssignmentsCards */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Контент</h3>
        <TagAssignmentsCards tagId={tagId} />
      </div>
    </div>
  );
}

// ─── Вкладка «Почта без приглашения» ───────────────────────────────────────

function TagEmailWithoutInvitationTab({ tagId }: { tagId: number }) {
  const { query } = useCustom<RawEmailWithoutInvitationRow[]>({
    url: `${resolveApiUrl()}/tags/${tagId}/email-without-invitation`,
    method: 'get',
    queryOptions: { staleTime: 60_000 },
  });

  const rawRows = query.data?.data ?? [];
  // Добавляем id = userId, чтобы DataTable мог работать с rowKey
  const rows: EmailWithoutInvitationRow[] = rawRows.map((r) => ({ ...r, id: r.userId }));
  const isLoading = query.isLoading;

  const columns: Column<EmailWithoutInvitationRow>[] = [
    {
      key: 'id',
      header: 'Пользователь',
      render: (_, row) => {
        const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || `#${row.userId}`;
        return (
          <a
            href={`/users/show/${row.userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:underline flex items-center gap-1"
          >
            {name}
            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
          </a>
        );
      },
    },
    {
      key: 'email',
      header: 'Email',
      render: (value) => <span className="text-sm">{String(value)}</span>,
    },
    {
      key: 'createdAt',
      header: 'Дата',
      width: '130px',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground py-10 border border-dashed rounded-lg">
        <Mail className="h-8 w-8 opacity-40" />
        <span>Нет пользователей, подтвердивших почту без приглашения</span>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={rows}
      total={rows.length}
      page={1}
      pageSize={rows.length}
      isLoading={false}
      onPageChange={() => {}}
      emptyMessage="Нет данных"
      rowKey="id"
    />
  );
}

// ─── Главный компонент ──────────────────────────────────────────────────────

export function TagsShow() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const tagId = id ? parseInt(id, 10) : undefined;

  const { query } = useOne<TagItem>({
    resource: 'tags',
    id: tagId,
    queryOptions: { enabled: Boolean(tagId) },
  });

  const tag = query.data?.data;
  const isLoading = query.isLoading;

  if (!tagId) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-muted-foreground">
        <Tag className="h-12 w-12 opacity-30" />
        <p>Тег не найден</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tag) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-muted-foreground">
        <Tag className="h-12 w-12 opacity-30" />
        <p>Тег не найден</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Шапка */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go({ to: '/tags' })}
          className="-ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Теги
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div
          className="h-9 w-9 rounded-md border border-border shrink-0"
          style={{ backgroundColor: tag.color }}
        />
        <div>
          <h1 className="text-2xl font-bold leading-none">{tag.name}</h1>
          {tag.description && (
            <p className="text-sm text-muted-foreground mt-1">{tag.description}</p>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => go({ to: `/tags/edit/${tagId}` })}
          >
            Редактировать
          </Button>
        </div>
      </div>

      {/* Табы */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Основное</TabsTrigger>
          <TabsTrigger value="assignments">Привязки</TabsTrigger>
          <TabsTrigger value="email-no-invite">Почта без приглашения</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <TagOverviewTab tag={tag} />
        </TabsContent>

        <TabsContent value="assignments" className="mt-4">
          <TagAssignmentsTab tagId={tagId} />
        </TabsContent>

        <TabsContent value="email-no-invite" className="mt-4">
          <TagEmailWithoutInvitationTab tagId={tagId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
